import { Close, Delete, Edit } from "@mui/icons-material";
import { IconButton } from "@mui/material";
import { EntryContext } from "components/tags/types";
import { cn } from "components/lib/utils";
import { useState } from "react";

import {
  FieldValueGroupReferenceRangeEditorProps,
  FieldValueGroupReferenceRangeEditor,
} from "./FieldValueGroupReferenceRangeEditor";
import { FieldValueGroupReference } from "generated/graphql";
import { FormFieldValueGroupUI } from "Backend";

type FieldValueGroupReferenceLineProps = {
  activeReferenceId: number | undefined;
  activeBlockIndex: number | null | undefined;
  reference: FieldValueGroupReference;
  valueGroup: FormFieldValueGroupUI;
  page: string;
  onClick: (referenceContext: EntryContext) => void;
  onDelete: (referenceId: number) => void;
  canDelete: boolean | undefined;
  isDisabled: boolean;
  dataPendo: string | undefined;
  rangeEditorProps: Omit<FieldValueGroupReferenceRangeEditorProps, "dataPendo" | "reference" | "onDelete">;
};

export function FieldValueGroupReferenceLine({
  activeReferenceId,
  activeBlockIndex,
  reference,
  valueGroup,
  page,
  onClick,
  onDelete,
  canDelete,
  isDisabled,
  dataPendo,
  rangeEditorProps,
}: FieldValueGroupReferenceLineProps) {
  const [editingReference, setEditingReference] = useState(false);
  const referenceIsActive =
    activeReferenceId !== undefined ? activeReferenceId === reference.id : activeBlockIndex === reference.blockIndex;
  return (
    <div key={reference.id}>
      <div
        className="flex items-center"
        onClick={() => {
          const params: EntryContext = {
            valueGroupId: valueGroup.id,
            referenceId: reference.id,
          };
          onClick(params);
        }}>
        {referenceIsActive ? (
          <span className="h-4 min-w-1 bg-yellow-400 opacity-50"></span>
        ) : (
          <span className="invisible h-4 min-w-1"></span>
        )}
        <p className="text-gray-400 inline-block mx-0.5 text-xxs">
          {reference.pageRangeEnd
            ? `[Pgs ${reference.pageRangeStart ? reference.pageRangeStart : page}-${reference.pageRangeEnd}]`
            : ""}
        </p>
        <p
          title={reference.blockText || "Full Page"}
          className={cn(
            "grow cursor-pointer overflow-hidden whitespace-nowrap text-ellipsis py-1 text-xxs font-mono font-medium hover:underline"
          )}>
          {reference.blockText || "Full Page"}
        </p>
        {referenceIsActive && (
          <IconButton
            disableRipple
            size="small"
            sx={{
              p: 0,
            }}
            onClick={(e) => {
              e.stopPropagation();
              setEditingReference(!editingReference);
            }}>
            {editingReference ? <Close className="h-4 w-4 py-0" /> : <Edit className="h-4 w-4" />}
          </IconButton>
        )}

        {canDelete && (
          <IconButton
            color="warning"
            size="small"
            disabled={isDisabled}
            onClick={(e) => {
              e.stopPropagation();
              onDelete(reference.id);
            }}>
            <Delete sx={{ height: 12, width: 12 }} />
          </IconButton>
        )}
      </div>
      {referenceIsActive && editingReference && (
        <FieldValueGroupReferenceRangeEditor
          dataPendo={dataPendo}
          reference={reference}
          onDelete={onDelete}
          {...rangeEditorProps}
        />
      )}
    </div>
  );
}
